import React, { useState, useCallback, useRef, useEffect } from "react";
import { Box, Switch } from "@mui/material";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import {
  ViewKanbanOutlined,
  CalendarMonthOutlined,
  ViewListOutlined,
  SearchOutlined,
  GroupsOutlined,
  AddRounded,
  CloseRounded,
} from "@mui/icons-material";
import useTranslation from "../../../../shared/hooks/useTranslation";

/** Threshold below which desktop filter bar wraps chips to a second row */
const NARROW_DESKTOP_WRAP_PX = 1600;

interface BoardFilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  activeFilter: string | null;
  onFilterChange: (filter: string | null) => void;
  viewMode: "board" | "calendar" | "list";
  onViewModeChange: (mode: "board" | "calendar" | "list") => void;
  showCancelled: boolean;
  onToggleCancelled: (value: boolean) => void;
  counts: {
    all: number;
    critical: number;
    overdue: number;
    myTasks: number;
    preventive: number;
  };
  /* Phase 3C: sidebar toggle + CTA props */
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  canManage: boolean;
  canWrite: boolean;
  onCreateWorkOrder: () => void;
  /** When true, hide the CTA button (phone: CTA is rendered in the header stats row instead) */
  hideCreateButton?: boolean;
}

interface FilterChip {
  key: string;
  label: string;
}

const BoardFilterBar = ({
  searchTerm,
  onSearchChange,
  activeFilter,
  onFilterChange,
  viewMode,
  onViewModeChange,
  showCancelled,
  onToggleCancelled,
  counts,
  sidebarCollapsed,
  onToggleSidebar,
  canManage,
  canWrite,
  onCreateWorkOrder,
  hideCreateButton,
}: BoardFilterBarProps) => {
  const { translate } = useTranslation("operations");
  const { isPhone, isTablet } = useBreakpoint();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [localSearch, setLocalSearch] = useState(searchTerm);

  // Narrow tablet detection (<900px) for collapsing search to icon
  const [isNarrowTablet, setIsNarrowTablet] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 900;
  });
  // Narrow desktop detection — chips wrap to 2nd row when there isn't enough room
  const [isNarrowDesktopWrap, setIsNarrowDesktopWrap] = useState(() => {
    if (typeof window === "undefined") return false;
    const w = window.innerWidth;
    return w >= 1280 && w < NARROW_DESKTOP_WRAP_PX;
  });
  useEffect(() => {
    const handleResize = () => {
      setIsNarrowTablet(window.innerWidth < 900);
      const w = window.innerWidth;
      setIsNarrowDesktopWrap(w >= 1280 && w < NARROW_DESKTOP_WRAP_PX);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setLocalSearch(searchTerm);
  }, [searchTerm]);

  const handleSearchInput = useCallback(
    (value: string) => {
      setLocalSearch(value);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 300);
    },
    [onSearchChange]
  );

  const chips: FilterChip[] = [
    { key: "all", label: translate("filters.all") },
    { key: "myTasks", label: translate("filters.myTasks") },
    { key: "critical", label: translate("filters.critical") },
    { key: "overdue", label: translate("filters.overdue") },
    { key: "preventive", label: translate("filters.preventive") },
  ];

  /* ─── Phone layout: filter chips + view mode toggle ─── */
  if (isPhone) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        {/* Scrollable filter chips */}
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            flexShrink: 1,
            minWidth: 0,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {chips.map((chip) => {
            const isActive = chip.key === "all" ? activeFilter === null : activeFilter === chip.key;
            return (
              <Box
                key={chip.key}
                onClick={() => onFilterChange(chip.key === "all" ? null : chip.key)}
                sx={{
                  padding: "5px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  fontFamily: "'Sora', sans-serif",
                  fontSize: "11px",
                  fontWeight: isActive ? 600 : 500,
                  backgroundColor: isActive ? "#0C1117" : "#F2F0EB",
                  color: isActive ? "#F7F5F0" : "#4A4F57",
                  transition: "all 0.15s ease",
                }}
              >
                {chip.label}
              </Box>
            );
          })}
        </Box>

        {/* View mode toggle — 3 grouped icon buttons */}
        <Box
          sx={{
            display: "flex",
            backgroundColor: "#F2F0EB",
            borderRadius: "6px",
            overflow: "hidden",
            flexShrink: 0,
            marginLeft: "auto",
          }}
        >
          {(["board", "calendar", "list"] as const).map((mode) => (
            <Box
              key={mode}
              onClick={() => onViewModeChange(mode)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "28px",
                height: "28px",
                cursor: "pointer",
                backgroundColor: viewMode === mode ? "#0C1117" : "transparent",
                color: viewMode === mode ? "#F7F5F0" : "#4A4F57",
                transition: "all 0.15s ease",
              }}
            >
              {mode === "board" ? (
                <ViewKanbanOutlined sx={{ fontSize: 14 }} />
              ) : mode === "calendar" ? (
                <CalendarMonthOutlined sx={{ fontSize: 14 }} />
              ) : (
                <ViewListOutlined sx={{ fontSize: 14 }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  /* ─── Reusable chip row renderer ─── */
  const renderChips = () => (
    <>
      {chips.map((chip) => {
        const isActive = chip.key === "all" ? activeFilter === null : activeFilter === chip.key;
        return (
          <Box
            key={chip.key}
            onClick={() => onFilterChange(chip.key === "all" ? null : chip.key)}
            sx={{
              padding: isTablet ? "5px 12px" : "6px 14px",
              borderRadius: "6px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              fontFamily: "'Sora', sans-serif",
              fontSize: isTablet ? "11px" : "12px",
              fontWeight: isActive ? 600 : 500,
              backgroundColor: isActive ? "#0C1117" : "#F2F0EB",
              color: isActive ? "#F7F5F0" : "#4A4F57",
              transition: "all 0.15s ease",
              "&:hover": !isActive
                ? { backgroundColor: "#E8E5DF" }
                : {},
            }}
          >
            {chip.label}
          </Box>
        );
      })}
    </>
  );

  /* ─── Narrow desktop layout: 2 rows (actions top, chips bottom) ─── */
  if (isNarrowDesktopWrap && !isTablet) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", flexShrink: 0 }}>
        {/* Row 1: search + view toggle + cancelled + teams toggle + CTA */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Search bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#F7F5F0",
              borderRadius: "6px",
              padding: "0 10px",
              height: "32px",
              width: "180px",
              border: `1.5px solid ${searchFocused ? "#0C1117" : "transparent"}`,
              transition: "border-color 0.2s ease",
              flexShrink: 0,
            }}
          >
            <SearchOutlined sx={{ fontSize: 14, color: "#8C9099" }} />
            <input
              type="text"
              value={localSearch}
              onChange={(e) => handleSearchInput(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder={translate("board.searchPlaceholder")}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "12px",
                fontFamily: "'Sora', sans-serif",
                color: "#0C1117",
                background: "transparent",
                marginLeft: "8px",
              }}
            />
          </Box>

          {/* View mode toggle */}
          <Box
            sx={{
              display: "flex",
              backgroundColor: "#F2F0EB",
              borderRadius: "6px",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {(["board", "calendar", "list"] as const).map((mode) => (
              <Box
                key={mode}
                onClick={() => onViewModeChange(mode)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  backgroundColor: viewMode === mode ? "#0C1117" : "transparent",
                  color: viewMode === mode ? "#F7F5F0" : "#4A4F57",
                  transition: "all 0.15s ease",
                  "&:hover": viewMode !== mode ? { backgroundColor: "#E8E5DF" } : {},
                }}
              >
                {mode === "board" ? (
                  <ViewKanbanOutlined sx={{ fontSize: 16 }} />
                ) : mode === "calendar" ? (
                  <CalendarMonthOutlined sx={{ fontSize: 16 }} />
                ) : (
                  <ViewListOutlined sx={{ fontSize: 16 }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Show Cancelled toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
            <Switch
              size="small"
              checked={showCancelled}
              onChange={(_, v) => onToggleCancelled(v)}
              sx={{
                "& .MuiSwitch-thumb": { width: 14, height: 14 },
                "& .MuiSwitch-switchBase": { padding: "4px" },
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#0C1117" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#0C1117" },
              }}
            />
            <Box
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontSize: "11px",
                fontWeight: 500,
                color: showCancelled ? "#0C1117" : "#8C9099",
                whiteSpace: "nowrap",
              }}
            >
              {translate("filters.showCancelled")}
            </Box>
          </Box>

          {/* Teams toggle button */}
          {canManage && (
            <>
              <Box sx={{ width: "1px", height: "24px", backgroundColor: "#E8E5DF", flexShrink: 0 }} />
              <Box
                onClick={onToggleSidebar}
                sx={{
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "6px",
                  cursor: "pointer",
                  flexShrink: 0,
                  backgroundColor: sidebarCollapsed ? "#EDEAE4" : "#0C1117",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: sidebarCollapsed ? "#E8E5DF" : "#1a1f28",
                  },
                }}
              >
                <GroupsOutlined
                  sx={{
                    fontSize: 16,
                    color: sidebarCollapsed ? "#4A4F57" : "#F7F5F0",
                  }}
                />
              </Box>
            </>
          )}

          {/* CTA: + New Work Order (pushed to end) */}
          {canWrite && !hideCreateButton && (
            <>
              <Box sx={{ width: "1px", height: "24px", backgroundColor: "#E8E5DF", flexShrink: 0 }} />
              <Box
                onClick={onCreateWorkOrder}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: "#D4572B",
                  color: "#FFFFFF",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: "13px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  marginLeft: "auto",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: "#BF4D26",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(212,87,43,0.25)",
                  },
                }}
              >
                <AddRounded sx={{ fontSize: 16 }} />
                {translate("createWorkOrder")}
              </Box>
            </>
          )}
        </Box>

        {/* Row 2: Filter chips — always fully visible */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {renderChips()}
        </Box>
      </Box>
    );
  }

  /* ─── Desktop (wide) / Tablet layout: single row ─── */
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: isTablet ? "8px" : "12px",
        flexShrink: 0,
      }}
    >
      {/* Filter chips — horizontal scroll, no wrapping */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: isTablet ? "8px" : "12px",
        overflowX: "auto",
        flexShrink: 1,
        minWidth: 0,
        scrollbarWidth: "none",            /* Firefox */
        "&::-webkit-scrollbar": { display: "none" }, /* Chrome/Safari/Edge */
      }}>
        {renderChips()}
      </Box>

      {/* Right section: search + view toggle + cancelled + teams toggle + CTA */}
      <Box sx={{ display: "flex", alignItems: "center", gap: isTablet ? "8px" : "10px", marginLeft: "auto", flexShrink: 0 }}>
        {/* Search bar — at narrow tablet (<900px), collapses to icon toggle */}
        {isTablet && isNarrowTablet && !searchExpanded ? (
          <Box
            onClick={() => setSearchExpanded(true)}
            sx={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: "#F7F5F0",
              transition: "all 0.15s ease",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#E8E5DF" },
            }}
          >
            <SearchOutlined sx={{ fontSize: 14, color: "#8C9099" }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#F7F5F0",
              borderRadius: "6px",
              padding: "0 10px",
              height: "32px",
              width: isTablet ? "140px" : "180px",
              border: `1.5px solid ${searchFocused ? "#0C1117" : "transparent"}`,
              transition: "border-color 0.2s ease, width 0.2s ease",
              flexShrink: 0,
            }}
          >
            <SearchOutlined sx={{ fontSize: 14, color: "#8C9099" }} />
            <input
              type="text"
              value={localSearch}
              onChange={(e) => handleSearchInput(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => {
                setSearchFocused(false);
                // Collapse back to icon on narrow tablet if search is empty
                if (isTablet && isNarrowTablet && !localSearch.trim()) {
                  setSearchExpanded(false);
                }
              }}
              placeholder={translate("board.searchPlaceholder")}
              autoFocus={isTablet && isNarrowTablet && searchExpanded}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "12px",
                fontFamily: "'Sora', sans-serif",
                color: "#0C1117",
                background: "transparent",
                marginLeft: "8px",
              }}
            />
            {/* Close button for expanded search on narrow tablet */}
            {isTablet && isNarrowTablet && searchExpanded && (
              <Box
                onClick={() => {
                  setSearchExpanded(false);
                  handleSearchInput("");
                }}
                sx={{ cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0 }}
              >
                <CloseRounded sx={{ fontSize: 14, color: "#8C9099" }} />
              </Box>
            )}
          </Box>
        )}

        {/* View mode toggle */}
        <Box
          sx={{
            display: "flex",
            backgroundColor: "#F2F0EB",
            borderRadius: "6px",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {(["board", "calendar", "list"] as const).map((mode) => (
            <Box
              key={mode}
              onClick={() => onViewModeChange(mode)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                backgroundColor: viewMode === mode ? "#0C1117" : "transparent",
                color: viewMode === mode ? "#F7F5F0" : "#4A4F57",
                transition: "all 0.15s ease",
                "&:hover": viewMode !== mode ? { backgroundColor: "#E8E5DF" } : {},
              }}
            >
              {mode === "board" ? (
                <ViewKanbanOutlined sx={{ fontSize: 16 }} />
              ) : mode === "calendar" ? (
                <CalendarMonthOutlined sx={{ fontSize: 16 }} />
              ) : (
                <ViewListOutlined sx={{ fontSize: 16 }} />
              )}
            </Box>
          ))}
        </Box>

        {/* Show Cancelled toggle — hidden at tablet to save space */}
        {!isTablet && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
            <Switch
              size="small"
              checked={showCancelled}
              onChange={(_, v) => onToggleCancelled(v)}
              sx={{
                "& .MuiSwitch-thumb": { width: 14, height: 14 },
                "& .MuiSwitch-switchBase": { padding: "4px" },
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#0C1117" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#0C1117" },
              }}
            />
            <Box
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontSize: "11px",
                fontWeight: 500,
                color: showCancelled ? "#0C1117" : "#8C9099",
                whiteSpace: "nowrap",
              }}
            >
              {translate("filters.showCancelled")}
            </Box>
          </Box>
        )}

        {/* Divider before Teams toggle — desktop only (tablet has TabletTeamsBar) */}
        {!isTablet && canManage && (
          <Box sx={{ width: "1px", height: "24px", backgroundColor: "#E8E5DF", flexShrink: 0 }} />
        )}

        {/* Teams toggle button — toggles sidebar (desktop only) */}
        {!isTablet && canManage && (
          <Box
            onClick={onToggleSidebar}
            sx={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              cursor: "pointer",
              flexShrink: 0,
              backgroundColor: sidebarCollapsed ? "#EDEAE4" : "#0C1117",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: sidebarCollapsed ? "#E8E5DF" : "#1a1f28",
              },
            }}
          >
            <GroupsOutlined
              sx={{
                fontSize: 16,
                color: sidebarCollapsed ? "#4A4F57" : "#F7F5F0",
              }}
            />
          </Box>
        )}

        {/* Divider before CTA */}
        {canWrite && !hideCreateButton && (
          <Box sx={{ width: "1px", height: "24px", backgroundColor: "#E8E5DF", flexShrink: 0 }} />
        )}

        {/* CTA: + New Work Order */}
        {canWrite && !hideCreateButton && (
          <Box
            onClick={onCreateWorkOrder}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: isTablet ? "7px 14px" : "8px 20px",
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: "#D4572B",
              color: "#FFFFFF",
              fontFamily: "'Sora', sans-serif",
              fontSize: isTablet ? "12px" : "13px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: "#BF4D26",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(212,87,43,0.25)",
              },
            }}
          >
            <AddRounded sx={{ fontSize: 16 }} />
            {isTablet ? translate("board.createShort") : translate("createWorkOrder")}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BoardFilterBar;
